import { Edit, Eye, Trash2 } from 'lucide-react';
import type { Source } from '../types/sources.types';
import { SOURCE_TYPE_LABELS } from '../types/sources.types';
import { useAuthorization } from '@/core/hooks/useAuthorization';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Switch } from '@/shared/components/ahkinia/Switch/switch';
import { Button } from '@/shared/components/ui/button';
import type { DataTableColumn } from '@/shared/components/ui/data-table';
import { DataTable } from '@/shared/components/ui/data-table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { PERMISSIONS } from '@/shared/constants/permissions';

interface PaginationMeta {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  firstItemOnPage?: number;
  lastItemOnPage?: number;
}

interface SourcesTableProps {
  data: Source[];
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onViewDetail?: (sourceId: string) => void;
  onEdit?: (sourceId: string) => void;
  onToggle?: (sourceId: string) => void;
  onDelete?: (sourceId: string, sourceName: string) => void;
}

function formatLastSync(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatLatency(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function formatCoverage(pct: number | null): string {
  if (pct === null || pct === undefined) return '—';
  return `${Math.round(pct)}%`;
}

function formatVolume(v: number | null): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('es-MX');
}

export function SourcesTable({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  onViewDetail,
  onEdit,
  onToggle,
  onDelete,
}: SourcesTableProps) {
  const { hasPermission } = useAuthorization();
  const canToggle = hasPermission(PERMISSIONS.SOURCES_TOGGLE);

  const columns: DataTableColumn<Source>[] = [
    {
      key: 'name',
      header: 'Nombre de la fuente',
      render: (row) => (
        <span data-testid={`source-name-${row.id}`} className="text-light text-sm">
          {row.name}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Tipo de fuente',
      render: (row) => (
        <span data-testid={`source-type-${row.id}`} className="text-primary text-sm">
          {SOURCE_TYPE_LABELS[row.type] ?? row.type}
        </span>
      ),
    },
    {
      key: 'lastSyncAt',
      header: 'Última sincronización',
      render: (row) => (
        <span data-testid={`source-lastsync-${row.id}`} className="text-primary text-sm">
          {formatLastSync(row.lastSyncAt)}
        </span>
      ),
    },
    {
      key: 'isEnabled',
      header: 'Estado',
      render: (row) => (
        <div data-testid={`source-status-${row.id}`}>
          <Switch
            checked={row.isEnabled}
            disabled={!canToggle}
            onCheckedChange={() => onToggle?.(row.id)}
          />
        </div>
      ),
    },
    {
      key: 'volume',
      header: 'Volumen',
      render: (row) => (
        <span data-testid={`source-volume-${row.id}`} className="text-primary text-sm">
          {formatVolume(row.volume)}
        </span>
      ),
    },
    {
      key: 'latencyMs',
      header: 'Latencia',
      render: (row) => (
        <span data-testid={`source-latency-${row.id}`} className="text-primary text-sm">
          {formatLatency(row.latencyMs)}
        </span>
      ),
    },
    {
      key: 'coveragePercentage',
      header: 'Cobertura',
      render: (row) => (
        <span data-testid={`source-coverage-${row.id}`} className="text-primary text-sm">
          {formatCoverage(row.coveragePercentage)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      render: (row) => (
        <div data-testid={`source-actions-${row.id}`} className="flex items-center gap-2">
          <Button
            data-testid={`view-source-${row.id}`}
            iconImg={<Eye />}
            title="Ver detalle"
            onClick={() => onViewDetail?.(row.id)}
          />
          <Protected permissions={PERMISSIONS.SOURCES_EDIT}>
            <Button
              data-testid={`edit-source-${row.id}`}
              iconImg={<Edit />}
              title="Editar"
              onClick={() => onEdit?.(row.id)}
            />
          </Protected>
          <Protected permissions={PERMISSIONS.SOURCES_DELETE}>
            {!row.isEnabled ? (
              <Button
                data-testid={`delete-source-${row.id}`}
                iconImg={<Trash2 />}
                title="Eliminar"
                variant="default"
                className="text-destructive hover:text-destructive"
                onClick={() => onDelete?.(row.id, row.name)}
              />
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        data-testid={`delete-source-disabled-${row.id}`}
                        iconImg={<Trash2 />}
                        title="Debe deshabilitarse antes de eliminar"
                        variant="default"
                        className="text-muted-foreground cursor-not-allowed opacity-50"
                        disabled
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Debe deshabilitarse antes de poder eliminar.</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Protected>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="sources-table">
      <DataTable
        title="Fuentes de información"
        subtitle={String(data.length)}
        rows={data}
        columns={columns}
        getRowKey={(row) => String(row.id)}
        pagination={pagination}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
